import API from '../context/API';

interface EarningsOptions {
  startDate?: string | any;
  endDate?: string | any;
  merchantId?: string;
  store?: string;
  includeComparison?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: any;
}

interface ApiResponse<T = any> {
  data?: T;
  [key: string]: any;
}

interface PlatformBreakdown {
  kiosk?: {
    orderCount?: number;
    revenue?: number;
  };
  mobile?: {
    orderCount?: number;
    revenue?: number;
  };
}

interface FulfillmentBreakdown {
  pickup?: {
    orderCount?: number;
    revenue?: number;
  };
  delivery?: {
    orderCount?: number;
    revenue?: number;
  };
}

interface FeeCategory {
  amount?: number;
  count?: number;
  percentage?: number;
}

interface FeeCategories {
  stripe?: FeeCategory;
  processing?: FeeCategory;
  tax?: FeeCategory;
  tips?: FeeCategory;
  discounts?: FeeCategory;
  [key: string]: FeeCategory | undefined;
}

interface EarningsSummary {
  totalRevenue?: number;
  totalFees?: number;
  netEarnings?: number;
  orderCount?: number;
  averageOrderValue?: number;
}

interface EarningsData {
  merchantName?: string;
  period?: {
    startDate?: string;
    endDate?: string;
  };
  summary?: EarningsSummary;
  platformBreakdown?: PlatformBreakdown;
  fulfillmentBreakdown?: FulfillmentBreakdown;
  feeCategories?: FeeCategories;
  data?: EarningsData;
  [key: string]: any;
}

interface DashboardSummaryResponse {
  merchantName: string;
  period: Record<string, any>;
  summary: Record<string, any>;
  platformBreakdown: Record<string, any>;
  fulfillmentBreakdown: Record<string, any>;
  feeCategories: Record<string, any>;
}

interface NetEarningsResponse {
  totalRevenue: number;
  totalFees: number;
  netEarnings: number;
  feePercentage: number;
}

interface StripeFeeResponse {
  amount: number;
  count: number;
  percentage: number;
}

const EARNINGS_ENDPOINTS = {
  MERCHANT_EARNINGS: '/merchant/{merchantId}/earnings',
  FEE_BREAKDOWN: '/analytics/fee-breakdown',
  ADMIN_MERCHANT_EARNINGS: '/admin/merchant-earnings',
  ADMIN_MERCHANT_EARNINGS_EXPORT: '/admin/merchant-earnings/export'
};

const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  retryMultiplier: 2
};

const apiCallWithRetry = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: Record<string, any> = {}
): Promise<ApiResponse> => {
  const { maxRetries, retryDelay, retryMultiplier } = RETRY_CONFIG;
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        throw new Error('No access token found');
      }

      const response = await API.get<ApiResponse>(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...params,
          _t: Date.now(),
        },
        ...options
      });

      if (!response.data) {
        throw new Error('Invalid response structure: missing data');
      }

      return response;
    } catch (error: any) {
      lastError = error;

      if (error.response?.status === 401) {
        throw error;
      }

      if (error.response?.status >= 400 && error.response?.status < 500 &&
          error.response?.status !== 408 && error.response?.status !== 429) {
        throw error;
      }

      if (attempt === maxRetries) {
        throw error;
      }

      const delay = retryDelay * Math.pow(retryMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));

      console.warn(`Earnings API call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
    }
  }

  throw lastError;
};

const buildEarningsParams = (options: EarningsOptions = {}): Record<string, any> => {
  const {
    startDate,
    endDate,
    merchantId,
    store,
    includeComparison = false,
    ...otherParams
  } = options;

  const params: Record<string, any> = {};

  if (startDate && endDate) {
    params.startDate = typeof startDate === 'string' ? startDate : startDate.format('YYYY-MM-DD');
    params.endDate = typeof endDate === 'string' ? endDate : endDate.format('YYYY-MM-DD');
  }

  if (merchantId) {
    params.merchantId = merchantId;
  }
  if (store) {
    params.store = store;
  }

  if (includeComparison) {
    params.includeComparison = includeComparison;
  }

  Object.assign(params, otherParams);

  return params;
};

class EarningsAPI {
  static async getMerchantEarnings(merchantId: string, options: EarningsOptions = {}): Promise<any> {
    if (!merchantId) {
      throw new Error('Merchant ID is required');
    }

    const endpoint = EARNINGS_ENDPOINTS.MERCHANT_EARNINGS.replace('{merchantId}', merchantId);
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(endpoint, params);
    return response.data;
  }

  static async getFeeBreakdown(options: EarningsOptions = {}): Promise<any> {
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(EARNINGS_ENDPOINTS.FEE_BREAKDOWN, params);
    return response.data;
  }

  static async getAdminMerchantEarnings(options: EarningsOptions = {}): Promise<any> {
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(EARNINGS_ENDPOINTS.ADMIN_MERCHANT_EARNINGS, params);
    return response.data;
  }

  static async exportAdminMerchantEarningsCSV(options: EarningsOptions = {}): Promise<string> {
    const params = buildEarningsParams(options);
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found');
    }

    const response = await API.get<string>(EARNINGS_ENDPOINTS.ADMIN_MERCHANT_EARNINGS_EXPORT, {
      headers: { Authorization: `Bearer ${token}` },
      params,
      responseType: 'text'
    });
    return response.data;
  }

  static async getPlatformBreakdown(merchantId: string, options: EarningsOptions = {}): Promise<any> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.platformBreakdown || {};
  }

  static async getFulfillmentBreakdown(merchantId: string, options: EarningsOptions = {}): Promise<any> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.fulfillmentBreakdown || {};
  }

  static async getFeeCategories(merchantId: string, options: EarningsOptions = {}): Promise<any> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.feeCategories || {};
  }

  static async getStripeFees(merchantId: string, options: EarningsOptions = {}): Promise<StripeFeeResponse> {
    const feeCategories = await this.getFeeCategories(merchantId, options);
    return feeCategories.stripe || { amount: 0, count: 0, percentage: 0 };
  }

  static async getNetEarnings(merchantId: string, options: EarningsOptions = {}): Promise<NetEarningsResponse> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const summary = earnings.data?.summary || {};

    return {
      totalRevenue: summary.totalRevenue || 0,
      totalFees: summary.totalFees || 0,
      netEarnings: summary.netEarnings || 0,
      feePercentage: summary.totalRevenue > 0 ?
        ((summary.totalFees || 0) / summary.totalRevenue * 100) : 0
    };
  }

  static async getDashboardSummary(merchantId: string, options: EarningsOptions = {}): Promise<DashboardSummaryResponse> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const data = earnings.data || {};

    return {
      merchantName: data.merchantName || 'Unknown',
      period: data.period || {},
      summary: data.summary || {},
      platformBreakdown: data.platformBreakdown || {},
      fulfillmentBreakdown: data.fulfillmentBreakdown || {},
      feeCategories: data.feeCategories || {}
    };
  }

  static async exportEarningsCSV(merchantId: string, options: EarningsOptions = {}): Promise<string> {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const data = earnings.data || {};

    const headers = [
      'Date Range',
      'Merchant Name',
      'Total Revenue',
      'Total Fees',
      'Net Earnings',
      'Order Count',
      'Average Order Value',
      'Kiosk Orders',
      'Kiosk Revenue',
      'Mobile Orders',
      'Mobile Revenue',
      'Pickup Orders',
      'Pickup Revenue',
      'Delivery Orders',
      'Delivery Revenue',
      'Stripe Fees',
      'Processing Fees',
      'Tax',
      'Tips',
      'Discounts'
    ];

    const rows = [
      [
        `${data.period?.startDate || ''} to ${data.period?.endDate || ''}`,
        data.merchantName || '',
        data.summary?.totalRevenue || 0,
        data.summary?.totalFees || 0,
        data.summary?.netEarnings || 0,
        data.summary?.orderCount || 0,
        data.summary?.averageOrderValue || 0,
        data.platformBreakdown?.kiosk?.orderCount || 0,
        data.platformBreakdown?.kiosk?.revenue || 0,
        data.platformBreakdown?.mobile?.orderCount || 0,
        data.platformBreakdown?.mobile?.revenue || 0,
        data.fulfillmentBreakdown?.pickup?.orderCount || 0,
        data.fulfillmentBreakdown?.pickup?.revenue || 0,
        data.fulfillmentBreakdown?.delivery?.orderCount || 0,
        data.fulfillmentBreakdown?.delivery?.revenue || 0,
        data.feeCategories?.stripe?.amount || 0,
        data.feeCategories?.processing?.amount || 0,
        data.feeCategories?.tax?.amount || 0,
        data.feeCategories?.tips?.amount || 0,
        data.feeCategories?.discounts?.amount || 0
      ]
    ];

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

export default EarningsAPI;
export { EARNINGS_ENDPOINTS, RETRY_CONFIG };
export type { EarningsOptions, ApiResponse, EarningsData, DashboardSummaryResponse, NetEarningsResponse, StripeFeeResponse };
